import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { authService } from '@/services';
import { trackAuth, trackForm, trackCTA } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import userActions from '@/redux/actions/user';

export default function SignIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Track page view and scroll depth
  useScrollTracking('SignIn');

  // Track login page load
  useEffect(() => {
    trackAuth.loginStart('signin');
  }, []);

  // Track form field interactions
  const handleFieldFocus = (fieldName: string) => {
    if (!formStarted) {
      trackForm.formStart('signin-form', 'Sign In Form');
      setFormStarted(true);
    }
    trackForm.fieldFocus('signin-form', fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Track login submit
    trackAuth.loginSubmit('email');
    trackForm.formSubmit('signin-form', 'Sign In Form', true);

    try {
      const response = await authService.login({ username, password });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Login failed');
      }

      // Track successful login
      trackAuth.loginSuccess(response.data.user?.id);

      // Dispatch user data to Redux store
      dispatch(userActions.login(response.data.user) as any);
      // Redirect based on user role
      if (response.data.user.role === 'admin') {
        navigate('/dashboard');
      } else {
        navigate('/courses');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      // Track login failure
      trackAuth.loginFailure(err.message || 'Unknown error');
      trackForm.formError('signin-form', 'Sign In Form', err.message || 'Login failed');
      setError(err.message || 'Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Navigation />

      {/* Sign In Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Branding/Info */}
            <div className="hidden lg:block">
              <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
                <span className="text-purple-400">🚀 Start Learning Today</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
                <span className="block">Welcome Back to</span>
                <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  AI Mastery Hub
                </span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Continue your journey to master AI. 
                Your next breakthrough is just one login away.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Lifetime Access</h3>
                    <p className="text-sm text-gray-400">Learn at your own pace with unlimited course access</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Expert Community</h3>
                    <p className="text-sm text-gray-400">Connect with fellow learners and industry experts</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-cyan-600 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Real Results</h3>
                    <p className="text-sm text-gray-400">Join thousands who've transformed their careers</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Sign In Form */}
            <div className="w-full max-w-md mx-auto">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-20"></div>
                
                {/* Form Container */}
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/20">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-2">Sign In</h2>
                    <p className="text-gray-400">Enter your credentials to access your account</p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Username Field */}
                    <div>
                      <label htmlFor="username" className="block text-sm font-medium mb-2">
                        Username
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          onFocus={() => handleFieldFocus('username')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                          placeholder="your-username"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Password Field */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => handleFieldFocus('password')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                          placeholder="Enter your password"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
                          disabled={loading}
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div className="flex items-center justify-between text-sm">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-white/10 bg-white/5 text-purple-600 focus:ring-purple-500/20 focus:ring-2"
                          disabled={loading}
                        />
                        <span className="text-gray-300">Remember me</span>
                      </label>
                      <Link to="/forgot-password" className="text-purple-400 hover:text-purple-300 transition-colors">
                        Forgot password?
                      </Link>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all text-lg group flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing In...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Sign Up Link */}
                  <p className="text-center text-sm text-gray-400 mt-6">
                    Don't have an account?{' '}
                    <Link to="/sign-up" className="text-purple-400 hover:text-purple-300 font-semibold transition-colors">
                      Sign up for free
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="simple" />
    </div>
  );
}
